import {
  EditOutlined,
  FormOutlined,
  RocketOutlined,
  TagsOutlined,
  MessageOutlined,
  FileTextOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASelect, ATextArea, ASwitch } from '../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Space, Typography, DatePicker } from 'antd';
import { useToast } from '../../../../hooks/toast';
import { schemaCrmCampaign } from '../../schemas/yup.crm-campaign.schema';
import {
  CrmCampaignCategory,
  CrmMessageType,
  CrmScheduleType,
  ICrmCampaign,
  IResponseAudience,
  getCrmTemplates,
  patchCrmCampaign,
  postCrmCampaign,
} from '../../../../http/crm';
import AudienceBuilder, { AudienceFilters } from '../../AudienceBuilder';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

interface ICampaignCreateEditProps {
  open: boolean;
  close: () => void;
  campaign: ICrmCampaign | null;
  setCampaigns: React.Dispatch<React.SetStateAction<ICrmCampaign[]>>;
  onSaved?: () => void;
}

const CATEGORY_OPTIONS: { value: CrmCampaignCategory; label: string }[] = [
  { value: 'LEAD', label: 'Captação / Leads' },
  { value: 'RETENTION', label: 'Retenção' },
  { value: 'BIRTHDAY', label: 'Aniversário' },
  { value: 'TREATMENT_RETURN', label: 'Retorno Tratamento' },
  { value: 'OFFICE', label: 'Escritório / Interno' },
  { value: 'OTHERS', label: 'Outros' },
];

const MESSAGE_TYPE_OPTIONS: { value: CrmMessageType; label: string }[] = [
  { value: 'TEXT', label: 'Texto livre' },
  { value: 'TEMPLATE', label: 'Template WhatsApp Aprovado' },
];

const SCHEDULE_TYPE_OPTIONS: { value: CrmScheduleType; label: string }[] = [
  { value: 'NOW', label: 'Imediato' },
  { value: 'SCHEDULED', label: 'Agendado' },
];

const CampaignCreateEdit: React.FC<ICampaignCreateEditProps> = ({
  open,
  close,
  campaign,
  setCampaigns,
  onSaved,
}) => {
  const { addToast } = useToast();
  const isEditing = !!campaign;

  const [category, setCategory] = useState<CrmCampaignCategory>('OTHERS');
  const [name, setName] = useState<string>('');
  const [messageType, setMessageType] = useState<CrmMessageType>('TEXT');
  const [whatsappTemplateName, setWhatsappTemplateName] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [messageBody, setMessageBody] = useState<string>('');
  const [scheduleType, setScheduleType] = useState<CrmScheduleType>('NOW');
  const [scheduledAt, setScheduledAt] = useState<Dayjs | null>(null);
  const [audienceFilters, setAudienceFilters] = useState<AudienceFilters | null>(null);
  const [audienceResult, setAudienceResult] = useState<IResponseAudience | null>(null);
  const [status, setStatus] = useState<boolean>(true);

  const [templates, setTemplates] = useState<{ id: string; name: string; body_text: string }[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);

  const loadTemplates = useCallback(async () => {
    if (messageType !== 'TEMPLATE') return;
    setLoadingTemplates(true);
    try {
      const res = await getCrmTemplates(100, 0, '', undefined, true);
      setTemplates(res.data || []);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar templates.',
      });
    } finally {
      setLoadingTemplates(false);
    }
  }, [messageType, addToast]);

  useEffect(() => {
    if (messageType === 'TEMPLATE') {
      loadTemplates();
    }
  }, [messageType, loadTemplates]);

  const resetForm = useCallback(() => {
    setCategory('OTHERS');
    setName('');
    setMessageType('TEXT');
    setWhatsappTemplateName('');
    setTemplateId('');
    setMessageBody('');
    setScheduleType('NOW');
    setScheduledAt(null);
    setAudienceFilters(null);
    setAudienceResult(null);
    setStatus(true);
  }, []);

  const handleAudienceChange = useCallback(
    (filters: AudienceFilters | null, audience: IResponseAudience | null) => {
      setAudienceFilters(filters);
      setAudienceResult(audience);
    },
    [],
  );

  const handleCreate = useCallback(async () => {
    const payload: Record<string, unknown> = {
      name,
      category,
      message_type: messageType,
      whatsapp_template_name: messageType === 'TEMPLATE' ? whatsappTemplateName || null : null,
      template_id: messageType === 'TEMPLATE' ? templateId || null : null,
      message_body: messageType === 'TEXT' ? messageBody || null : null,
      schedule_type: scheduleType,
      scheduled_at: scheduleType === 'SCHEDULED' && scheduledAt ? scheduledAt.toISOString() : null,
      audience_filters: audienceFilters ?? null,
      total_target: audienceResult?.count ?? 0,
      status: 'DRAFT',
    };

    try {
      await schemaCrmCampaign.validate(payload);
      const data = await postCrmCampaign(payload);
      setCampaigns((state) => [...state, data]);
      addToast({
        type: 'success',
        title: 'Campanha criada',
        description: 'Campanha criada com sucesso. Vá para a lista para iniciá-la.',
      });
      if (onSaved) onSaved();
      close();
    } catch (err: any) {
      if (err?.name === 'ValidationError') {
        addToast({
          type: 'error',
          title: 'Campos obrigatórios',
          description: err.errors?.[0] ?? 'Verifique os campos obrigatórios.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao criar campanha',
          description: err?.response?.data?.error ?? 'Erro desconhecido.',
        });
      }
    }
  }, [
    name,
    category,
    messageType,
    whatsappTemplateName,
    templateId,
    messageBody,
    scheduleType,
    scheduledAt,
    audienceFilters,
    audienceResult,
    setCampaigns,
    addToast,
    close,
    onSaved,
  ]);

  const handleEdit = useCallback(async () => {
    if (!campaign?.id) return;

    const payload: Record<string, unknown> = {
      name,
      category,
      message_type: messageType,
      whatsapp_template_name: messageType === 'TEMPLATE' ? whatsappTemplateName || null : null,
      template_id: messageType === 'TEMPLATE' ? templateId || null : null,
      message_body: messageType === 'TEXT' ? messageBody || null : null,
      schedule_type: scheduleType,
      scheduled_at: scheduleType === 'SCHEDULED' && scheduledAt ? scheduledAt.toISOString() : null,
      audience_filters: audienceFilters ?? null,
      total_target: audienceResult?.count ?? campaign.total_target ?? 0,
    };

    try {
      await schemaCrmCampaign.validate(payload);
      const data = await patchCrmCampaign(campaign.id, payload);
      setCampaigns((state) => state.map((e) => (e.id !== data.id ? e : data)));
      addToast({
        type: 'info',
        title: 'Campanha atualizada',
        description: 'Alterações salvas com sucesso.',
      });
      if (onSaved) onSaved();
      close();
    } catch (err: any) {
      if (err?.name === 'ValidationError') {
        addToast({
          type: 'error',
          title: 'Campos obrigatórios',
          description: err.errors?.[0] ?? 'Verifique os campos obrigatórios.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro ao atualizar',
          description: err?.response?.data?.error ?? 'Erro desconhecido.',
        });
      }
    }
  }, [
    campaign,
    name,
    category,
    messageType,
    whatsappTemplateName,
    templateId,
    messageBody,
    scheduleType,
    scheduledAt,
    audienceFilters,
    audienceResult,
    setCampaigns,
    addToast,
    close,
    onSaved,
  ]);

  useEffect(() => {
    if (!open) return;

    if (campaign) {
      setCategory((campaign.category as CrmCampaignCategory) ?? 'OTHERS');
      setName(campaign.name ?? '');
      setMessageType((campaign.message_type as CrmMessageType) ?? 'TEXT');
      setWhatsappTemplateName(campaign.whatsapp_template_id ?? '');
      setTemplateId((campaign as any)?.template_id ?? '');
      setMessageBody(campaign.message_body ?? '');
      setScheduleType(campaign.schedule_type ?? 'NOW');
      setScheduledAt(campaign.scheduled_at ? dayjs(campaign.scheduled_at) : null);
      setAudienceFilters((campaign.audience_filters as AudienceFilters) ?? null);
      setStatus(campaign.status !== 'COMPLETED');
      return;
    }

    resetForm();
  }, [open, campaign, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> Editar Campanha
          </>
        ) : (
          <>
            <FormOutlined /> Nova Campanha de WhatsApp
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={880}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space size={8}>
              <FileTextOutlined /> Dados Básicos
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={
                  <Space size={6}>
                    <TagsOutlined style={{ color: '#2f54eb' }} /> Categoria
                  </Space>
                }
                required
              >
                <ASelect
                  value={category}
                  onChange={(v) => setCategory(v as CrmCampaignCategory)}
                  options={CATEGORY_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item
                label={
                  <Space size={6}>
                    <RocketOutlined style={{ color: '#1890ff' }} /> Nome da campanha
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Ex: Campanha de aniversário - Agosto"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={150}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={10}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ScheduleOutlined style={{ color: '#722ed1' }} /> Agendamento
                  </Space>
                }
              >
                <ASelect
                  value={scheduleType}
                  onChange={(v) => setScheduleType(v as CrmScheduleType)}
                  options={SCHEDULE_TYPE_OPTIONS}
                />
              </Form.Item>
            </Col>

            {scheduleType === 'SCHEDULED' && (
              <Col xs={24} sm={14}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <ScheduleOutlined style={{ color: '#faad14' }} /> Data e horário
                    </Space>
                  }
                  required
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    value={scheduledAt}
                    onChange={(d) => setScheduledAt(d as Dayjs | null)}
                    format="DD/MM/YYYY HH:mm"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space size={8}>
              <MessageOutlined /> Conteúdo da Mensagem
            </Space>
          }
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={10}>
              <Form.Item
                label={
                  <Space size={6}>
                    <MessageOutlined style={{ color: '#13c2c2' }} /> Tipo da mensagem
                  </Space>
                }
                required
              >
                <ASelect
                  value={messageType}
                  onChange={(v) => {
                    setMessageType(v as CrmMessageType);
                    setTemplateId('');
                    setWhatsappTemplateName('');
                    setMessageBody('');
                  }}
                  options={MESSAGE_TYPE_OPTIONS}
                />
              </Form.Item>
            </Col>

            {messageType === 'TEMPLATE' && (
              <>
                <Col xs={24} sm={14}>
                  <Form.Item
                    label={
                      <Space size={6}>
                        <FileTextOutlined style={{ color: '#eb2f96' }} /> Template WhatsApp
                      </Space>
                    }
                    required
                  >
                    <ASelect
                      showSearch
                      loading={loadingTemplates}
                      placeholder="Selecione um template aprovado"
                      allowClear
                      value={templateId}
                      onChange={(v) => {
                        const selected = templates.find((t) => t.id === v);
                        setTemplateId(v ?? '');
                        setWhatsappTemplateName(selected?.name ?? '');
                      }}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={templates.map((t) => ({
                        label: t.name,
                        value: t.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="Nome do template (Meta)" style={{ marginBottom: 0 }}>
                    <AInput
                      placeholder="Ex: birthday_campaign_v1"
                      value={whatsappTemplateName}
                      onChange={(e) => setWhatsappTemplateName(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {messageType === 'TEXT' && (
              <Col xs={24}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <MessageOutlined style={{ color: '#52c41a' }} /> Mensagem
                    </Space>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <ATextArea
                    rows={6}
                    placeholder="Escreva a mensagem que será enviada. Use \{\{cliente\}\} para variáveis."
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    maxLength={4096}
                    showCount
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <AudienceBuilder
          value={audienceFilters}
          onChange={handleAudienceChange}
          category={category}
        />

        {isEditing && (
          <Card size="small" style={{ marginTop: 12 }}>
            <Form.Item label="Status da campanha" style={{ marginBottom: 0 }}>
              <Space align="center" size={10}>
                <ASwitch
                  checked={status}
                  checkedChildren="Ativa"
                  unCheckedChildren="Inativa"
                  onChange={() => setStatus(!status)}
                />
                <Text type="secondary">
                  {status ? 'Campanha ativa' : 'Campanha inativa (não será disparada)'}
                </Text>
              </Space>
            </Form.Item>
          </Card>
        )}
      </Form>
    </AModal>
  );
};

export default CampaignCreateEdit;
